import type { ComponentChildren } from 'preact';
import { ChevronDown } from 'lucide-preact';
import { Icon } from './Icon';
import { cn } from '@/lib/cn';

export interface DisclosureProps {
  /** Texto del header — siempre visible. */
  summary: ComponentChildren;
  /** Contenido que aparece al expandir. */
  children: ComponentChildren;
  /** Estado inicial. Default cerrado. */
  defaultOpen?: boolean;
  /** Variante visual. `card` (default) tiene borde y padding como las cards
   *  operativas; `inline` es más liviano, sin borde, para anidar dentro de
   *  otro Card o sección. */
  variant?: 'card' | 'inline';
  className?: string;
}

/**
 * Bloque colapsable single-item — wrap nativo de `<details>` con tokens
 * del sistema.  Usar para "ver datos del cálculo", "ver detalles
 * técnicos", "ver más info" en páginas operativas. Para grupos con
 * varios items relacionados, usar `<Accordion>`.
 */
export function Disclosure({
  summary,
  children,
  defaultOpen = false,
  variant = 'card',
  className,
}: DisclosureProps) {
  return (
    <details
      open={defaultOpen}
      className={cn(
        'group',
        variant === 'card'
          ? 'rounded-md border border-border-subtle bg-surface'
          : '',
        className,
      )}
    >
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center justify-between gap-3 text-13 font-medium text-fg-strong outline-none',
          'transition-colors duration-med',
          'focus-visible:shadow-focus focus-visible:ring-1 focus-visible:ring-brand',
          variant === 'card'
            ? 'rounded-md px-4 py-3 hover:bg-surface-hover'
            : 'rounded-sm py-1 hover:text-fg-strong',
        )}
      >
        <span className="min-w-0">{summary}</span>
        <Icon
          as={ChevronDown}
          size={16}
          className="text-fg-muted transition-transform duration-med group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div
        className={cn(
          'text-13 text-fg-muted leading-relaxed',
          variant === 'card' ? 'border-t border-border-subtle px-4 py-3' : 'pt-2',
        )}
      >
        {children}
      </div>
    </details>
  );
}
